import type { MutationOptions, QueryClient } from "@tanstack/react-query";

import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { SiError, errorCategoryMap } from "app/pages/sales-intelligence/utils/errors/types";

import type { IFiltersSelectionDto } from "../../types/dto";
import { dedupeFilterKeys } from "../../utils/filterKeys";
import { advancedSearchFilteringQueryKeys } from "../queryKeyFactory";
import type { SelectedFiltersContext } from "../types";

export const createFiltersSelectionMutationOptions = (
    queryClient: QueryClient,
): MutationOptions<void, Error, readonly string[], SelectedFiltersContext> => ({
    mutationKey: advancedSearchFilteringQueryKeys.filtersSelectionMutation(),
    scope: { id: advancedSearchFilteringQueryKeys.filtersSelectionMutation().join("/") },
    mutationFn: (filters) =>
        salesApiService.advancedSearchFiltering.setPinnedFilters({
            filters: dedupeFilterKeys(filters),
        }),
    onMutate: async (filters) => {
        const queryKey = advancedSearchFilteringQueryKeys.filtersSelection();

        await queryClient.cancelQueries({ queryKey });

        const previous = queryClient.getQueryData<IFiltersSelectionDto>(queryKey);

        queryClient.setQueryData<IFiltersSelectionDto>(queryKey, { filters: dedupeFilterKeys(filters) });

        return { previous };
    },
    onError: (error, _filters, context) => {
        if (context?.previous) {
            queryClient.setQueryData<IFiltersSelectionDto>(
                advancedSearchFilteringQueryKeys.filtersSelection(),
                context.previous,
            );
        }

        trackError(
            new SiError(
                "Failed to set pinned filters",
                {
                    category: errorCategoryMap.api,
                    operation: "setPinnedFilters",
                },
                { cause: error },
            ),
        );
    },
    onSettled: () => {
        void queryClient.invalidateQueries({
            queryKey: advancedSearchFilteringQueryKeys.filtersSelection(),
        });
    },
});
